import Icon from "@/Components/Icon/Icon";
import SoldOut from "@/Components/SoldOut";
import { useSetModal } from "@/shared/atoms/useModal";
import usePosOrderMenuValues from "@/shared/atoms/usePosOrderMenuValuesAtom";
import usePosMenuGetColor from "@/shared/hooks/usePosMenuGetColor";
import usePosMenuGetPriceHook from "@/shared/hooks/usePosMenuGetPriceHook";
import {
  getFieldByLang,
  isGreaterThanZero,
  formatPrice,
} from "@/shared/utils/helpers";
import {
  CartItem,
  Category,
  Menu,
  PosSettings,
} from "@/shared/utils/interfaces";
import { QueryObserverResult } from "@tanstack/query-core";
import { useTranslation } from "react-i18next";
import File from "../Base/File";
import {
  DeliveryMethods,
  MenuItems,
  ProductStatusEnums,
} from "@/shared/utils/enum";
import usePosOrderDeliveryMethod from "@/shared/atoms/usePosOrderDeliveryMethodAtom";
import { useSetPosOrderSearch } from "@/shared/atoms/usePosOrderSearchAtom";
import { useAddCartItem } from "@/shared/hooks/useCartHook";
import { cn } from "@/shared/utils/cn";
import Center from "../Base/Center";

interface PosMenuItemWrapperProps {
  bg?: string;
  fontSize?: number;
  selected?: boolean;
  show?: boolean;
  isLast?: boolean;
  isFavorite?: boolean;
}

export const MenuItemWrapper = ({
  bg,
  selected,
  show,
  isLast,
  isFavorite,
  className = "",
  ...props
}: PosMenuItemWrapperProps & { className?: string; [key: string]: any }) => (
  <div
    className={cn(
      "relative h-32 cursor-pointer overflow-hidden rounded-lg px-2 hover:opacity-85",
      show ? "flex flex-col items-start" : "hidden",
      className
    )}
    style={{
      backgroundColor: isFavorite ? "var(--white)" : bg,
      border: isFavorite
        ? `${selected ? "0.5rem" : "0.2rem"} solid var(--error)`
        : selected
          ? "0.5rem solid var(--success)"
          : isLast
            ? "0.1rem solid var(--dark)"
            : undefined,
    }}
    {...props}
  />
);

interface PosMenuItemProps {
  visible?: boolean;
  isLast?: boolean;
  isFavorite?: boolean;
  item: Category | CartItem;
  settings: PosSettings;
  bgColor: string;
}

const PosMenuItem = ({
  visible = true,
  isLast,
  isFavorite = false,
  item,
  settings,
  bgColor: bgColorProp,
}: PosMenuItemProps) => {
  const { t } = useTranslation();
  const [deliveryMethod] = usePosOrderDeliveryMethod();
  const [{ selectedCat }, setPosOrderMenuValues] = usePosOrderMenuValues();
  const { show_product_images, font_size, is_always_ask_for_topping } =
    settings;
  const isProduct = item.item_type === MenuItems.PRODUCT;
  const isCategory = item.item_type === MenuItems.CATEGORY;
  const imageSrc = item.image_cloudinary_id;
  const name = item.name;

  const selected = isCategory && selectedCat?.item_id === item.item_id;
  const hasChild = isGreaterThanZero(item.sub_content);
  const getPrice = usePosMenuGetPriceHook();
  const getColor = usePosMenuGetColor();
  const setModal = useSetModal();
  const setPosOrderSearch = useSetPosOrderSearch();
  const addCartItem = useAddCartItem();

  const bgColor = getColor(item ?? {}, "bg") || bgColorProp;
  const textColor = getColor(item, "text") || "#fff";

  const handleClick = () => {
    const parentId = (item as CartItem)?.parent_id ?? selectedCat?.item_id;

    if (isProduct) {
      const methods = [DeliveryMethods.DELIVERY, DeliveryMethods.TAKEAWAY];
      if (
        isGreaterThanZero(item.sub_content) &&
        (methods.includes(deliveryMethod) || is_always_ask_for_topping)
      ) {
        setModal("posOrderSelectToppings", {
          product: { ...item, parent_id: parentId },
        });
      } else {
        if (parseFloat((item as CartItem).price ?? "0") > 0) {
          addCartItem({ ...item, parent_id: parentId, quantity: 1 });
        } else {
          setModal("posOrderProduct", {
            product: { ...item, parent_id: parentId },
          });
        }
      }
    } else if (isCategory) {
      setPosOrderMenuValues((draft) => {
        draft.selectedCat =
          selectedCat?.item_id === item.item_id ? null : (item as Category);
        draft.selectedTab = 1;
      });
      setPosOrderSearch((draft) => {
        draft.text = "";
      });
    }
  };

  if (isLast && isProduct)
    return (
      <MenuItemWrapper
        onClick={() =>
          setModal("menuProduct", {
            parentId: (item as CartItem).parent_id,
            callback: async (
              refetch: () => Promise<QueryObserverResult<Menu, Error>>
            ) => {
              const { data } = await refetch();
              const newCategory = data.navigation_content.find(
                (category) => category.item_id === selectedCat.item_id
              );
              setPosOrderMenuValues((draft) => {
                draft.selectedCat = newCategory;
              });
            },
          })
        }
        bg="var(--light)"
        show
        isLast
      >
        <div className="flex h-full items-center text-primary-dark">
          <div className="mr-2 flex items-center rounded-[0.5rem] bg-grey p-4">
            <Icon className="h-8 w-8 fill-primary-dark" variants="plus" />
          </div>
          <h4>{t("addProduct")}</h4>
        </div>
      </MenuItemWrapper>
    );

  return (
    <MenuItemWrapper
      onClick={handleClick}
      bg={bgColor}
      fontSize={font_size}
      selected={selected}
      show={visible}
      isFavorite={isFavorite}
    >
      <div
        className="relative flex h-full w-full items-center"
        style={{ color: isFavorite ? "var(--text)" : textColor }}
      >
        <div className="flex w-[75%] flex-col gap-2">
          <div className="name">
            <Center className="justify-start gap-2 font-semibold">
              {isFavorite && (
                <Icon
                  variants="heart"
                  className="size-8 shrink-0 fill-error md:size-10"
                />
              )}
              {getFieldByLang(name)}
              {isProduct && hasChild && (
                <Icon
                  variants="carrotDown"
                  className="text-white ml-2 w-6 fill-white -mt-2"
                />
              )}
            </Center>
          </div>
          {!isCategory && (
            <div className="price">{formatPrice(getPrice(item))}</div>
          )}
        </div>
        {show_product_images && imageSrc && (
          <div className="flex w-[25%] justify-end">
            <File
              size={"6rem"}
              isDeletable={false}
              onChange={() => {}}
              src={imageSrc}
              canUpload={false}
              hideOnError={true}
            />
          </div>
        )}
        {isProduct &&
          (item as CartItem)?.status_enum > ProductStatusEnums.AVAILABLE && (
            <div
              className="absolute top-[50%] left-[50%]"
              style={{ transform: "translate(-40%,-25%)" }}
            >
              <SoldOut />
            </div>
          )}
      </div>
    </MenuItemWrapper>
  );
};

export default PosMenuItem;
